import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { useChecklistWorkspace } from '../ChecklistWorkspaceSection/hooks/useChecklistWorkspace';
import { Group } from '../ChecklistWorkspaceSection/components/types';
import { tagService, Tag } from '@/services/checklists';

type ChecklistWorkspaceReturn = ReturnType<typeof useChecklistWorkspace>;

interface ChecklistContextValue extends ChecklistWorkspaceReturn {
  loadInitialData: (groups: Group[]) => void;
  availableTags: Tag[];
  loadingTags: boolean;
  isEditMode?: boolean;
  initialGroupIds: Set<string>;
}

const ChecklistContext = createContext<ChecklistContextValue | null>(null);

interface ChecklistProviderProps {
  children: ReactNode;
  isEditMode?: boolean;
}

export function ChecklistProvider({ children, isEditMode = false }: ChecklistProviderProps) {
  const workspace = useChecklistWorkspace();
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const initialGroupIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedInitialDataRef = useRef(false);

  useEffect(() => {
    const loadTags = async () => {
      setLoadingTags(true);
      try {
        const tags = await tagService.getAllActiveTags();
        setAvailableTags(tags);
      } catch {
        // Ignore errors loading tags
      } finally {
        setLoadingTags(false);
      }
    };

    loadTags();
  }, []);

  useEffect(() => {
    if (isEditMode && workspace.groups.length > 0 && !hasLoadedInitialDataRef.current) {
      initialGroupIdsRef.current = new Set(workspace.groups.map(g => g.id));
      hasLoadedInitialDataRef.current = true;
    }
  }, [workspace.groups, isEditMode]);

  return (
    <ChecklistContext.Provider value={{
      ...workspace,
      availableTags,
      loadingTags,
      isEditMode,
      initialGroupIds: initialGroupIdsRef.current
    }}>
      {children}
    </ChecklistContext.Provider>
  );
}

export function useChecklistContext() {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error('useChecklistContext must be used within ChecklistProvider');
  }
  return context;
}
